/**
 * FooterStrip — the popup footer with progress bars.
 * Lot G: Prompts bar now opens the FooterSettingsPanel popover.
 */
import { useRef, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { Box, Button, ClickAwayListener, Paper, Popper } from '@mui/material';
import type { AllowanceFigures, Cadence } from '@traaker/shared';
import type { Brand, Tenant } from '@traaker/shared';
import { FooterBar } from './FooterBar';
import { FooterSettingsPanel } from './FooterSettingsPanel';
import type { AgencyFooterFigures, FooterChange } from './FooterSettingsPanel';

export interface FooterStripProps {
  promptFigures: (AllowanceFigures & { cadence?: Cadence; creditsPerMonth?: number }) | null;
  conversationFigures: AllowanceFigures | null;
  promptFull?: boolean;
  conversationFull?: boolean;
  showConversations?: boolean;
  tab: 'prompts' | 'conversations' | 'coverage';
  onAddPrompt?: () => void;
  onAddConversation?: () => void;
  brand?: Pick<Brand, 'brandId' | 'auditConfig'>;
  tenant?: Pick<Tenant, 'tenantId' | 'tier' | 'settings'>;
  agencyFigures?: AgencyFooterFigures;
  conversationCreditsPerMonth?: number;
  onLaunch?: () => void;
  /** A footer write succeeded: the popup reloads its figures (the popover stays open). */
  onFooterSaved?: (change: FooterChange) => void;
  /** When true, the Prompts popover opens immediately on mount. */
  initialPopoverOpen?: boolean;
  /** When true, the Conversations popover opens on mount, as soon as its bar and panel are there. */
  initialConvPopoverOpen?: boolean;
  /** The Prompts bar's numbers when it has its own (agency: credits per month, « Manuel »). */
  promptNums?: string;
  /** Lot I3: the Conversations bar's numbers and its popover (`ConversationsFooterPanel`). */
  conversationNums?: string;
  conversationPanel?: ReactNode;
}

export function FooterStrip({
  promptFigures,
  conversationFigures,
  promptFull,
  conversationFull,
  showConversations = false,
  tab,
  onAddPrompt,
  onAddConversation,
  brand,
  tenant,
  agencyFigures,
  conversationCreditsPerMonth,
  onLaunch,
  onFooterSaved,
  initialPopoverOpen,
  initialConvPopoverOpen,
  promptNums,
  conversationNums,
  conversationPanel,
}: FooterStripProps) {
  const barRef = useRef<HTMLDivElement>(null);
  // State, not a ref: the bar can mount in the very render its popover opens in (opened on mount),
  // and a ref read during that render is still null.
  const [convBarEl, setConvBarEl] = useState<HTMLDivElement | null>(null);
  const [promptPopoverOpen, setPromptPopoverOpenRaw] = useState(initialPopoverOpen ?? false);
  // One popover at a time (UI-SPEC §2.24).
  const [convPopoverOpen, setConvPopoverOpen] = useState(initialConvPopoverOpen ?? false);
  const setPromptPopoverOpen = useCallback((v: boolean | ((p: boolean) => boolean)) => {
    setPromptPopoverOpenRaw((p) => {
      const next = typeof v === 'function' ? v(p) : v;
      if (next) setConvPopoverOpen(false);
      return next;
    });
  }, []);

  const showAddPrompt = tab === 'prompts' || tab === 'coverage';
  const showAddConv = tab === 'conversations';

  const handlePromptBarClick = useCallback(() => {
    setPromptPopoverOpen((p) => !p);
  }, [setPromptPopoverOpen]);

  const handleClickAway = useCallback((e: MouseEvent | TouchEvent) => {
    // Don't close when the click was on the bar itself (it will toggle)
    if (barRef.current?.contains(e.target as Node)) return;
    setPromptPopoverOpen(false);
  }, [setPromptPopoverOpen]);

  // Extract engine/tier info for the panel
  const enginesEnabled = brand?.auditConfig?.enginesEnabled;
  const tenantEnginesEnabled = tenant?.settings?.enabledEngines;
  const tier = tenant?.tier ?? 'basic';
  // Agency is credit-metered: no allowance, so the bars carry numbers only (a track would stay empty).
  const hideTrack = tier === 'agency';
  const brandId = brand?.brandId ?? '';

  // The panel needs figures with cadence; server always returns cadence on promptRequests.
  const panelFigures = promptFigures as
    | (AllowanceFigures & { cadence: Cadence; creditsPerMonth?: number })
    | null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2.25,
        py: 1.125,
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        boxShadow: '0 -4px 20px rgba(0,0,0,.05)',
        flexShrink: 0,
        position: 'relative',
      }}
    >
      {/* Prompts bar — anchors the popover */}
      <Box ref={barRef}>
        <FooterBar
          kind="prompts"
          figures={promptFigures}
          full={promptFull}
          nums={promptNums}
          hideTrack={hideTrack}
          onClick={handlePromptBarClick}
        />
      </Box>

      {/* Prompts settings popover */}
      <Popper
        open={promptPopoverOpen}
        anchorEl={barRef.current}
        placement="top-start"
        style={{ zIndex: 1300 }}
        modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper
            elevation={4}
            sx={{
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 1px 2px rgba(0,0,0,.04), 0 12px 36px rgba(0,0,0,.14)',
            }}
          >
            {panelFigures?.cadence !== undefined || agencyFigures ? (
              <FooterSettingsPanel
                figures={panelFigures}
                agencyFigures={agencyFigures}
                tier={tier}
                brandId={brandId}
                enginesEnabled={enginesEnabled}
                tenantEnginesEnabled={tenantEnginesEnabled}
                conversationCreditsPerMonth={conversationCreditsPerMonth}
                onSaved={(change) => onFooterSaved?.(change)}
                onLaunch={onLaunch}
              />
            ) : (
              <Box sx={{ p: 2, fontSize: 13, color: 'text.secondary' }}>
                {/* i18n: promptStudio.pop.loading */}
                Chargement…
              </Box>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>

      {/* Conversations bar */}
      {showConversations && (
        <Box ref={setConvBarEl}>
          <FooterBar
            kind="conversations"
            figures={conversationFigures}
            full={conversationFull}
            nums={conversationNums}
            hideTrack={hideTrack}
            onClick={conversationPanel ? () => { setPromptPopoverOpen(false); setConvPopoverOpen((o) => !o); } : undefined}
          />
        </Box>
      )}
      {conversationPanel && (
        <Popper
          open={showConversations && convPopoverOpen && !!convBarEl}
          anchorEl={convBarEl}
          placement="top-start"
          style={{ zIndex: 1300 }}
          modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
        >
          <ClickAwayListener
            onClickAway={(e) => {
              if (convBarEl?.contains(e.target as Node)) return;
              setConvPopoverOpen(false);
            }}
          >
            <Paper elevation={4} sx={{ borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,.04), 0 12px 36px rgba(0,0,0,.14)' }}>
              {conversationPanel}
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Add button */}
      {showAddPrompt && onAddPrompt && (
        <Button
          variant="contained"
          size="small"
          onClick={onAddPrompt}
          sx={{ fontWeight: 600, fontSize: 13 }}
        >
          {/* i18n: promptStudio.foot.addPrompt */}
          + Ajouter des prompts
        </Button>
      )}
      {showAddConv && onAddConversation && (
        <Button
          variant="contained"
          size="small"
          onClick={onAddConversation}
          sx={{ fontWeight: 600, fontSize: 13 }}
        >
          {/* i18n: promptStudio.foot.addConversation */}
          + Nouvelle conversation
        </Button>
      )}
    </Box>
  );
}
